/**
 * The occurrence counts of the Swift engine (CONVENTIONS §2.4, §3, divergence D5):
 * ForecastEngine's iterated count, MetricsCalculator's closed-form count, and
 * MetricsCalculator's n-th occurrence date. All schedules are anchored (R6).
 */
import {
  absoluteMonth, addMonths, dayOfMonth, daysBetween, daysInMonthOf, fromYmd, maxDay, minDay, monthsBetween, toYmd,
  daysInMonth, yearsBetween, type Day,
} from '../core/calendar.ts';
import { frequencyStep, scheduleDate } from '../models/frequency.ts';
import { effectivePaymentDay } from '../models/transaction.ts';
import type { Transaction } from '../types/domain.ts';

/**
 * ForecastEngine.countOccurrences(for:from:to:): occurrences anchored at max(from, start)
 * through min(to, endDate), both inclusive; with `includingEnd` false, also strictly before
 * `to` (the transaction's own endDate stays inclusive). Inactive → 0; oneTime → 1 regardless
 * of dates; `endAfterOccurrences` counts from the effective start; a non-advancing step → 1.
 */
export function countOccurrences(transaction: Transaction, from: Day, to: Day, includingEnd = true): number {
  if (!transaction.isActive) return 0;
  const step = frequencyStep(transaction.frequency);
  if (step === null) return 1;
  const start = maxDay(from, transaction.startDate);
  if (start > to) return 0;
  const end = transaction.endDate === null ? to : minDay(to, transaction.endDate);
  const limit = transaction.endAfterOccurrences;
  let count = 0;
  for (let date = start; date <= end && (includingEnd || date < to); date = scheduleDate(start, step, count)) {
    count += 1;
    if (limit !== null && count >= limit) break;
    if (step.count <= 0) break;
  }
  return count;
}

/**
 * MetricsCalculator._countOccurrences(for:upTo:): closed-form count of occurrences on or
 * before `date`. Monthly honours paymentDayOfMonth (0 = last day); the others divide the
 * elapsed days/months/years by the interval. Limited by endDate (recount at it) and
 * endAfterOccurrences.
 */
export function countOccurrencesUpTo(transaction: Transaction, date: Day): number {
  if (date < transaction.startDate) return 0;
  const start = transaction.startDate;
  const limited = (count: number): number => limitOccurrences(count, transaction, date);
  const frequency = transaction.frequency;
  switch (frequency) {
    case 'oneTime':
      return 1;
    case 'daily':
      return limited(daysBetween(start, date) + 1);
    case 'weekly':
      return limited(Math.trunc(daysBetween(start, date) / 7) + 1);
    case 'fortnightly':
      return limited(Math.trunc(daysBetween(start, date) / 14) + 1);
    case 'monthly':
      return limited(monthlyCount(transaction, date));
    case 'quarterly':
      return limited(Math.trunc(monthsBetween(start, date) / 3) + 1);
    case 'yearly':
      return limited(yearsBetween(start, date) + 1);
    default: {
      if (frequency.interval <= 0) return 0;
      const elapsed = {
        day: () => daysBetween(start, date),
        week: () => Math.trunc(daysBetween(start, date) / 7),
        month: () => monthsBetween(start, date),
        year: () => yearsBetween(start, date),
      }[frequency.unit]();
      return limited(Math.trunc(elapsed / frequency.interval) + 1);
    }
  }
}

/** Payments made by `date` for a monthly transaction paying on its effective payment day. */
function monthlyCount(transaction: Transaction, date: Day): number {
  const paymentDay = effectivePaymentDay(transaction);
  const firstInStartMonth = paymentDay === 0 || paymentDay >= dayOfMonth(transaction.startDate);
  const firstMonth = absoluteMonth(transaction.startDate) + (firstInStartMonth ? 0 : 1);
  const targetMonth = absoluteMonth(date);
  if (targetMonth < firstMonth) return 0;
  const monthLength = daysInMonthOf(date);
  const paidThisMonth = dayOfMonth(date) >= (paymentDay === 0 ? monthLength : Math.min(paymentDay, monthLength));
  return targetMonth - firstMonth + (paidThisMonth ? 1 : 0);
}

function limitOccurrences(count: number, transaction: Transaction, date: Day): number {
  if (transaction.endDate !== null && date > transaction.endDate) {
    return countOccurrencesUpTo(transaction, transaction.endDate);
  }
  const capped = transaction.endAfterOccurrences === null ? count : Math.min(count, transaction.endAfterOccurrences);
  return Math.max(0, capped);
}

/**
 * MetricsCalculator.getOccurrenceDate(for:occurrence:): the 0-based n-th occurrence,
 * anchored to the start date. Monthly lands on the effective payment day (clamped; 0 = last
 * day), starting next month when the payment day is before the start day.
 */
export function occurrenceDate(transaction: Transaction, occurrence: number): Day {
  const start = transaction.startDate;
  const step = frequencyStep(transaction.frequency);
  if (step === null) return start;
  if (transaction.frequency === 'monthly') {
    const paymentDay = effectivePaymentDay(transaction);
    const offset = paymentDay === 0 || paymentDay >= dayOfMonth(start) ? 0 : 1;
    const { year, month } = toYmd(addMonths(start, offset + occurrence));
    const length = daysInMonth(year, month);
    return fromYmd(year, month, paymentDay === 0 ? length : Math.min(paymentDay, length));
  }
  if (step.count <= 0) return start;
  return scheduleDate(start, step, occurrence);
}
